import { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { SeamlessChapterScroller } from './seamless-scroll';
import { attachSwipeNavigation } from './swipe-nav';
import { bibleState } from '@/state/bible-state';
import type { BibleLocation } from '@skjv/common';
import { getChapterId, getNextChapter, getPreviousChapter } from '@skjv/common';
import { publicPath } from '@utils/public-path';
import { isDesktop } from '@/desktop/desktop';

interface BibleReaderProps {
  /** Initial location to display */
  initialLocation?: BibleLocation;
  /** Base path for HTML chapter files */
  htmlBasePath?: string;
}

/**
 * Main Bible reader component with seamless chapter scrolling.
 * Uses SeamlessChapterScroller to manage infinite scroll between chapters.
 */
export const BibleReader = observer(function BibleReader({
  initialLocation,
  htmlBasePath = publicPath('html'),
}: BibleReaderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<SeamlessChapterScroller | null>(null);

  // Create scroller on mount (nav effect handles initialization)
  useEffect(() => {
    console.log(
      '[BibleReader] Mount effect running, container:',
      !!containerRef.current,
      'scroller:',
      !!scrollerRef.current,
    );

    if (!containerRef.current) return;

    // If scroller already exists for this container, don't recreate
    if (scrollerRef.current) return;

    console.log('[BibleReader] Creating new scroller');
    scrollerRef.current = new SeamlessChapterScroller(containerRef.current, {
      htmlBasePath,
    });

    // Note: Chapter change location updates are handled by the header observer in App.tsx
    // for better timing alignment with the bounce animation. The scroller's onChapterChange
    // is intentionally not set here.

    scrollerRef.current.onStrongsClick = (numbers, location, _el, englishWord) => {
      // Reading mode: word taps scroll instead of opening Strong's lookups
      if (bibleState.isReadingMode) return;
      bibleState.showStrongsDefinition(numbers, location, { englishWord });
    };

    scrollerRef.current.onVerseClick = (location) => {
      // Prefetch the KJV/NASB analysis verdict so the menu row is
      // present on first paint (cache hits resolve within a frame)
      bibleState.loadVerseAnalysisVerdict(location);
      bibleState.setInteractionLocation(location);
      bibleState.showVerseMenu();
    };

    scrollerRef.current.onPericopeClick = (location, title, anchorRect) => {
      bibleState.showPericopeStudyPrompt(location, title, anchorRect);
    };

    // Provide bookmark data to scroller for displaying indicators.
    // Bookmarks are native-only (DESKTOP-PRD §3.8) — the web reader shows
    // no indicators even if legacy localStorage still holds bookmarks.
    scrollerRef.current.getBookmarksAt = (location) => {
      return isDesktop() ? bibleState.getBookmarksAt(location) : [];
    };

    // NOTE: Don't call initialize here - let the nav effect handle it.
    // This ensures a single code path for all navigation (mount + external).

    const detachSwipe = attachSwipeNavigation(containerRef.current, {
      onNext: () => {
        const next = getNextChapter(bibleState.currentLocation);
        if (next) bibleState.setCurrentLocation(next);
      },
      onPrev: () => {
        const prev = getPreviousChapter(bibleState.currentLocation);
        if (prev) bibleState.setCurrentLocation(prev);
      },
    });

    return () => {
      detachSwipe();
      scrollerRef.current?.destroy();
      scrollerRef.current = null;
    };
  }, [htmlBasePath, initialLocation]);

  // Handle external navigation requests (e.g., user clicks bookmark, ToC, etc.)
  useEffect(() => {
    console.log('[BibleReader] Nav effect triggered:', {
      hasScroller: !!scrollerRef.current,
      isScrollUpdate: bibleState.isScrollUpdate,
      target: bibleState.currentLocation,
    });

    if (!scrollerRef.current) {
      console.log('[BibleReader] No scroller yet, skipping');
      return;
    }

    // Skip if this is a scroll-based update (not external navigation)
    if (bibleState.isScrollUpdate) {
      console.log('[BibleReader] Scroll update, skipping');
      return;
    }

    const target = bibleState.currentLocation;
    const targetChapterId = getChapterId(target);

    // Get the scroller's current loaded location (null if mid-init)
    const current = scrollerRef.current.getCurrentLocation();
    const currentChapterId = current ? getChapterId(current) : null;

    console.log('[BibleReader] Navigation decision:', {
      targetChapterId,
      currentChapterId,
      needsInit: !current || currentChapterId !== targetChapterId,
    });

    // If scroller is mid-initialization (current is null), always re-initialize
    // to ensure we navigate to the correct target
    if (!current) {
      console.log('[BibleReader] Scroller current=null, calling initialize');
      scrollerRef.current.initialize(target);
      return;
    }

    // Check if we need to load a different chapter
    if (currentChapterId !== targetChapterId) {
      console.log('[BibleReader] Different chapter, calling initialize');
      scrollerRef.current.initialize(target);
    } else if (current.verseIndex !== target.verseIndex) {
      // Same chapter, different verse - just scroll
      console.log('[BibleReader] Same chapter, scrolling to verse:', target.verseIndex);
      scrollerRef.current.scrollToVerse(target);
    } else {
      console.log('[BibleReader] Already at target, no action needed');
    }
  }, [
    bibleState.currentLocation.testamentIndex,
    bibleState.currentLocation.bookIndex,
    bibleState.currentLocation.chapterIndex,
    bibleState.currentLocation.verseIndex,
  ]);

  // Refresh bookmark indicators when bookmarks change
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.refreshBookmarkIndicators();
    }
  }, [bibleState.bookmarks.length, bibleState.bookmarks]);

  // Apply study passage outline when editing study changes
  useEffect(() => {
    if (!scrollerRef.current) return;

    const study = bibleState.editingStudy;
    if (study?.startingLocation) {
      scrollerRef.current.setStudyPassageOutline(
        study.startingLocation,
        study.endingLocation,
      );

      // If the passage chapter is already loaded, scroll to it and glow the whole passage
      if (scrollerRef.current.isChapterLoaded(study.startingLocation)) {
        setTimeout(() => {
          scrollerRef.current?.scrollToStudyPassage(study.startingLocation!);
        }, 200);
      }
    } else {
      scrollerRef.current.setStudyPassageOutline(null);
    }
  }, [
    bibleState.editingStudy?.startingLocation?.testamentIndex,
    bibleState.editingStudy?.startingLocation?.bookIndex,
    bibleState.editingStudy?.startingLocation?.chapterIndex,
    bibleState.editingStudy?.startingLocation?.verseIndex,
    bibleState.editingStudy?.endingLocation?.testamentIndex,
    bibleState.editingStudy?.endingLocation?.bookIndex,
    bibleState.editingStudy?.endingLocation?.chapterIndex,
    bibleState.editingStudy?.endingLocation?.verseIndex,
  ]);

  return (
    <div
      ref={containerRef}
      id="bible-reader"
      className="bible-reader-container"
      style={{
        fontSize: `${1.25 * bibleState.fontScale}rem`,
      }}
    />
  );
});

export default BibleReader;
